import type { WorkbenchComponentScores } from '@/hooks/useWorkbench';

export interface WeakestComponent {
  key: keyof WorkbenchComponentScores;
  value: number;
  /** Gewichteter Punkteverlust: (100 minus Wert) mal Gewicht. */
  loss: number;
}

/**
 * Der Bereich, der die meisten Punkte kostet.
 *
 * GEWICHTETER VERLUST, nicht kleinster Rohwert. Das ist die Hausregel aus
 * `src/lib/scoreBreakdown.ts`, und sie ist die einzige, die im Kopf eines
 * geschlossenen Aufklappers etwas taugt: der Titel mit 70 steht ueber der
 * Kategorie mit 35, weil er 25 statt 10 Prozent wiegt. Wer den kleinsten
 * Rohwert nennt, schickt den Nutzer regelmaessig an die falsche Stelle.
 *
 * Gleichstand entscheidet die Anzeigereihenfolge, also das hoehere Gewicht
 * zuerst. Determinismus ist hier kein Feinschliff: derselbe Score darf nicht
 * bei jedem Render einen anderen Bereich nennen.
 *
 * DIESE ZUSAGE HAENGT VOLLSTAENDIG AN DER REIHENFOLGE, IN DER DER AUFRUFER
 * `components` UEBERGIBT - `weakestComponent` selbst sortiert nichts, sie
 * iteriert nur `for...of` und behaelt bei Gleichstand den ERSTEN Treffer.
 * "Das hoehere Gewicht zuerst" ist also keine Eigenschaft dieser Funktion,
 * sondern eine Eigenschaft von `SCORE_COMPONENTS` in
 * `src/components/workbench/card/ScorePanel.tsx` (dort dokumentiert als
 * "STRENG GEWICHTSABSTEIGEND"). `scoreWeakest.test.ts` importiert
 * `SCORE_COMPONENTS` statt einer eigenen Kopie und pinnt zusaetzlich genau
 * diese absteigende Reihenfolge - sortierte jemand `SCORE_COMPONENTS` um,
 * ohne diesen Test zu beachten, wuerde die Determinismus-Zusage still
 * falsch.
 *
 * `null` heisst "nichts geht verloren", nicht "unbekannt".
 */
export function weakestComponent(
  post: WorkbenchComponentScores,
  components: ReadonlyArray<{ key: keyof WorkbenchComponentScores; weightPct: number }>,
): WeakestComponent | null {
  let best: WeakestComponent | null = null;
  for (const { key, weightPct } of components) {
    const value = post[key];
    const loss = ((100 - value) * weightPct) / 100;
    if (loss <= 0) continue;
    if (!best || loss > best.loss) best = { key, value, loss };
  }
  return best;
}
